/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { usePageFilterFollowers } from '@hyphengroup/cms-components/calculatedAttributes/store';
import { PINNED_TOP_HEIGHT_VAR } from '@hyphengroup/cms-components/utils/pinnedHeight';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BaseComponentProps, VerticalType } from '../types';
import { SLOT_COMPONENT_INDEX_ATTR } from './slotComponentIndex';
import {
  endsLoanFilterRange,
  hasLoanFilterSlot,
  type PageFilterFollowers,
} from '$/(cms)/_modules/loanScope';

/**
 * Whether the slot's owner governs this block. Which blocks the loan filter
 * moves is the calculation engine's answer, not this component's (CMS-1890).
 */
type Gate = (
  component: BaseComponentProps,
  followers: PageFilterFollowers,
) => boolean;
const isComparisonTable: Gate = (c) =>
  c.__typename === 'ComponentPagesComponentComparisonTable';

/**
 * Two slots share this component and answer to different owners: the loan
 * filter's follows the loan-scope engine; the Comparison Table's tabs are the
 * table's own and keep the range they always had.
 */
type Slot = { rendersNextTo: Gate; endsRange: Gate };
export const SLOTS = {
  'personal-loan-filter': {
    rendersNextTo: hasLoanFilterSlot,
    endsRange: endsLoanFilterRange,
  },
  'comparison-table-tabs': {
    rendersNextTo: isComparisonTable,
    endsRange: (c, followers) =>
      isComparisonTable(c, followers) ||
      c.__typename === 'ComponentPagesComponentCardListingPosition',
  },
} satisfies Record<string, Slot>;
export type SlotId = keyof typeof SLOTS;

/** The last block the slot's owner governs — where it stops sticking. */
const findLastOfComponentInList = (
  components: BaseComponentProps[],
  endsRange: Gate,
  followers: PageFilterFollowers,
) => {
  let componentIndex: string | undefined = undefined;
  components.forEach((comp, index) => {
    if (endsRange(comp, followers)) componentIndex = index.toString();
  });
  return componentIndex;
};

export const PortalPlace = ({
  component,
  vertical,
  baseComponents,
  extraStickyIds,
  id,
  zOffset = 0,
  fullWide,
  componentIndex,
}: {
  /** The block this slot is rendered next to. */
  component: BaseComponentProps;
  /**
   * That block's GA `component_index`, read back by what is portalled into the
   * slot (see `slotComponentIndex`).
   */
  componentIndex?: number;
  vertical?: VerticalType;
  baseComponents: BaseComponentProps[];
  extraStickyIds?: string[];
  id: SlotId;
  zOffset?: 0 | 1;
  fullWide?: boolean;
}) => {
  const stickyRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [stickyContentHeight, setStickyContentHeight] = useState(0);
  const slot = SLOTS[id];
  const followers = usePageFilterFollowers();
  // The scan walks the whole page, and this component renders twice per block
  // on every scroll-driven sticky state change.
  const lastIdContent = useMemo(
    () => findLastOfComponentInList(baseComponents, slot.endsRange, followers),
    [baseComponents, slot.endsRange, followers],
  );
  const isStickyEnabled = isSticky && !zOffset;

  useEffect(() => {
    const element = stickyRef.current;
    const stickyContentElement = document.getElementById(
      'top-page-sticky-content',
    );

    if (!element) return;

    let intersectionObserver: IntersectionObserver;

    const createIntersectionObserver = (topOffset: number) => {
      return new IntersectionObserver(
        ([entry]) => {
          const sticky = entry.boundingClientRect.top <= topOffset;
          setIsSticky(sticky);

          if (!fullWide) {
            const bodyWidth = isStickyEnabled
              ? `${document.body.clientWidth}px`
              : 'auto';
            const shift = isStickyEnabled
              ? `${entry.boundingClientRect.left}px`
              : '0';

            element.style.paddingLeft = shift;
            element.style.marginLeft = `-${shift}`;
            element.style.width = bodyWidth;
          }
        },
        {
          root: null,
          threshold: 0,
          rootMargin: `${-topOffset}px 0px -100% 0px`,
        },
      );
    };

    const calculateTotalHeight = () => {
      let totalHeight = 0;

      if (stickyContentElement && vertical !== 'PERSONAL_LOANS') {
        totalHeight += stickyContentElement.offsetHeight;
      }

      if (extraStickyIds && extraStickyIds.length > 0) {
        extraStickyIds.forEach((stickyId) => {
          const extraElement = document.getElementById(stickyId);
          if (extraElement) {
            totalHeight += extraElement.offsetHeight;
          }
        });
      }

      return totalHeight;
    };

    const checkHeight = () => {
      const newHeight = calculateTotalHeight();

      if (newHeight !== stickyContentHeight) {
        setStickyContentHeight(newHeight);
        intersectionObserver?.disconnect();
        intersectionObserver = createIntersectionObserver(newHeight);
        if (element) {
          intersectionObserver.observe(element);
        }
      }
    };

    const initialHeight = calculateTotalHeight();
    setStickyContentHeight(initialHeight);

    intersectionObserver = createIntersectionObserver(initialHeight);
    intersectionObserver.observe(element);

    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const elementsToObserve = [stickyContentElement].filter(Boolean);
    if (extraStickyIds && extraStickyIds.length > 0) {
      extraStickyIds.forEach((stickyId) => {
        const extraElement = document.getElementById(stickyId);
        if (extraElement) {
          elementsToObserve.push(extraElement);
        }
      });
    }

    if (elementsToObserve.length) {
      resizeObserver = new ResizeObserver(checkHeight);
      mutationObserver = new MutationObserver(checkHeight);

      elementsToObserve.forEach((element) => {
        resizeObserver?.observe(element!);
        mutationObserver?.observe(element!, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      });
    }

    return () => {
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [
    stickyContentHeight,
    vertical,
    extraStickyIds,
    isStickyEnabled,
    fullWide,
  ]);

  // The Sort & Filter rail and its mobile trigger pin under the loan bar, blocks
  // away, so the height it covers is published for them. Not gated on
  // `isStickyEnabled`: they reach their sticky line only once the bar has pinned.
  useEffect(() => {
    const node = stickyRef.current;
    // `PortalTo` fills the first element with this id; the empty slots of the
    // other blocks must not overwrite it.
    if (id !== 'personal-loan-filter' || !node) return;
    if (document.getElementById(id) !== node) return;

    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(
        PINNED_TOP_HEIGHT_VAR,
        `${node.offsetHeight + stickyContentHeight}px`,
      );

    publish();
    // The filter's own height changes, and `hidden` past the range drops it to 0.
    const resizeObserver = new ResizeObserver(publish);
    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
      root.style.removeProperty(PINNED_TOP_HEIGHT_VAR);
    };
  }, [id, stickyContentHeight]);

  useEffect(() => {
    if (!lastIdContent) return;

    const stickyElement = stickyRef.current;
    const endElement = document.getElementById(`content-${lastIdContent}`);

    if (!endElement || !stickyElement) return;

    let prevScrollY = window.scrollY;
    let isScrollingDown = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      isScrollingDown = currentScrollY > prevScrollY;
      prevScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const { bottom } = entry.boundingClientRect;

          if (isScrollingDown && bottom <= 0) {
            stickyElement.classList.add('hidden');
          } else if (!isScrollingDown) {
            stickyElement.classList.remove('hidden');
          }
        });
      },
      { threshold: [0, 0.1, 0.2, 0.5, 0.9, 1] },
    );

    observer.observe(endElement);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [lastIdContent]);

  if (
    vertical !== 'PERSONAL_LOANS' ||
    !slot.rendersNextTo(component, followers)
  ) {
    return null;
  }
  // Hack for PageContent, should be synchronized with PageContent.tsx styles
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const style: any = {
    '--personal-loan-filter-offset':
      'calc((100% - var(--breakpoints-content-narrow-3-lg)) / 2)',
  };

  return (
    <div
      ref={stickyRef}
      id={id}
      {...{ [SLOT_COMPONENT_INDEX_ATTR]: componentIndex }}
      style={{
        top: `${stickyContentHeight}px`,
        paddingInline:
          !!fullWide && isStickyEnabled
            ? `max(var(--spacing-padding-section-x), var(--personal-loan-filter-offset))`
            : '',
        ...style,
      }}
      className={classNames(
        {
          'shadow-md': isStickyEnabled,
        },
        {
          'z-[var(--z-index-personal-filter)]': zOffset === 0,
          'z-[calc(var(--z-index-personal-filter)_+_1)]': zOffset === 1,
          // full screen mode styles, mobile + desktop(md)
          'px-spacing/padding/section-x max-w-[--breakpoints-content-narrow-3-lg] w-full mx-auto lg:px-0':
            !!fullWide && !isStickyEnabled,
          'w-full': !!fullWide && isStickyEnabled,
        },
        'bg-fill/base',
        '[&>*:first-child]:flex [&>*]:hidden',
        'sticky -mb-spacing/gap/y',
        'transition-shadow duration-300',
      )}
    />
  );
};
